import React from 'react';
import {
  Switch,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Copy, Check } from 'lucide-mui';

interface ToggleSettingProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  description: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  rightElement?: React.ReactNode;
}

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

interface CopyButtonProps {
  text: string;
  onCopy?: () => void;
}

export const CopyButton: React.FC<CopyButtonProps> = ({ text, onCopy }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="bottom" arrow>
      <IconButton
        type="button"
        aria-label="Copy"
        onClick={handleCopy}
        disabled={copied}
        sx={{
          padding: 0.5,
          color: 'secondary.main',
          backgroundColor: 'background.paper',
          borderRadius: '8px',
          height: '30px',
          width: '30px',
          '&:hover': {
            backgroundColor: 'secondary.main',
            color: 'common.white',
          },
        }}
      >
        {copied ? (
          <Check sx={{ width: 16, height: 16 }} />
        ) : (
          <Copy sx={{ width: 16, height: 16 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export const ToggleSetting: React.FC<ToggleSettingProps> = ({
  checked,
  onChange,
  label,
  description,
  icon,
  children,
  rightElement,
}) => (
  <Box
    sx={{
      py: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 0 },
      }}
    >
      <Box sx={{ flex: 1, mr: { xs: 0, sm: 3 }, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon && (
            <Box
              sx={{
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                mr: 1.5,
                width: '28px',
                '& > svg': {
                  fontSize: 20,
                },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.25 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
        }}
      >
        {rightElement}
        {!children && !rightElement && (
          <Switch
            checked={checked}
            onChange={onChange}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'primary.main',
                opacity: 0.5,
              },
              '& .MuiSwitch-track': {
                opacity: 0.3,
              },
            }}
          />
        )}
      </Box>
    </Box>
    {children && <Box sx={{ pl: { xs: 0, sm: 5 } }}>{children}</Box>}
  </Box>
);

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  children,
}) => (
  <Box>
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        mb: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          py: 1.5,
          fontWeight: 500,
          color: 'text.primary',
          fontSize: '1.25rem',
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box>{children}</Box>
  </Box>
);

export const renderSetting = (
  label: string,
  value: boolean | string,
  onChange: (newValue: boolean | string) => void
) => {
  if (typeof value === 'boolean') {
    return (
      <Switch
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        color="primary"
        sx={{ ml: 1 }}
      />
    );
  }
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      sx={{ ml: 1 }}
    />
  );
};

export type SubSettings =
  | 'audioSettings'
  | 'videoSettings'
  | 'recordingControlSettings'
  | 'uiSettings'
  | 'otherSettings';

export const recordingSubSettings: SubSettings[] = [
  'audioSettings',
  'videoSettings',
  'recordingControlSettings',
  'otherSettings',
];
export const pluginSubSettings: SubSettings[] = recordingSubSettings.concat([
  'uiSettings',
]);

export default renderSetting;
